import { Modal, Select, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { components as ReactSelectComponents } from 'react-select';

import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { VALIDATION_ERRORS } from '@hero/constants';
import { ModalValidationErrors } from '@hero/molecules';
import { RoleSelect } from '@hero/organisms';
import { useCheckEmails, useCreateInvite } from '@hero/services/organization-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { isEmailValid } from '@hero/utils';

import * as S from './style';

function InviteUserModal({ isOpen, setIsOpen }) {
  const { t } = useTranslation();
  const { showInfo } = useNotification();
  const [emails, setEmails] = useState([]);
  const [role, setRole] = useState(null);
  const pageLayoutContentRef = useRef(null);
  const { addModalError, modalErrors, reset } = useValidationErrorsStore();
  const { mutateAsync: checkEmails, isLoading: isCheckEmailLoading } = useCheckEmails();
  const { mutateAsync: createInvite, isLoading: isInviteLoading } = useCreateInvite({
    onSuccess: () => {
      showInfo({
        message: t('inviteRequestSuccessfulMessage'),
        type: 'toast'
      });
      setIsOpen(false);
      setEmails([]);
      setRole(null);
      reset();
    }
  });

  const checkCustomErrors = async () => {
    let hasCustomError = false;
    if (emails?.length === 0) {
      await addModalError(
        <Trans
          i18nKey={VALIDATION_ERRORS.noEnteredInviteEmail}
          components={{ strong: <strong /> }}
        />
      );
      hasCustomError = true;
    }
    if (!role) {
      await addModalError(
        <Trans
          i18nKey={VALIDATION_ERRORS.noEnteredInviteRole}
          components={{ strong: <strong /> }}
        />
      );
      hasCustomError = true;
    }
    return hasCustomError;
  };

  const primaryButtonClickHandler = async () => {
    const hasCustomError = await checkCustomErrors();
    if (!hasCustomError) {
      await createInvite({
        invites: emails.map(email => ({
          email: email.value,
          role: role.value
        }))
      });
    }
  };

  const childrenI18Key = useMemo(() => {
    if (emails.length === 1) {
      return t('inviteSingleAmountUser', { amount: emails.length });
    }
    if (emails.length > 1) {
      return t('inviteMultiAmountUser', { amount: emails.length });
    }
    return t('inviteUser');
  }, [emails]);

  const onEmailsChange = async emails => {
    reset();
    setEmails(emails.filter(email => isEmailValid(email.value)));

    if (emails?.length) {
      const result = await checkEmails({ emails: [emails.pop().value] });

      if (result?.existingUsers?.length) {
        await addModalError(
          <Trans
            i18nKey={VALIDATION_ERRORS.existingOrganizationUser}
            components={{ strong: <strong /> }}
            values={{ email: result?.existingUsers[0] }}
          />,
          'error'
        );
        setEmails(emails);
      }
    }
  };

  const onEmailBlur = e => {
    const isUnique = !emails.find(email => email.value === e.target.value);
    if (e.target.value && isUnique) {
      return onEmailsChange([...emails, { label: e.target.value, value: e.target.value }]);
    }
    return null;
  };

  // const onEmailKeyDown = async e => {
  //   if (e.keyCode === 32 || e.keyCode === 188) {
  //     const email = e.target.value.trim().replaceAll(',', '');
  //     await onEmailsChange([...emails, { label: email, value: email }]);
  //   }
  // };

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={setIsOpen}
      title={t('inviteUserMulti')}
      primaryButtonProps={{
        children: childrenI18Key,
        onClick: primaryButtonClickHandler,
        loading: isInviteLoading,
        disabled: isCheckEmailLoading
      }}
      secondaryButtonProps={{ children: t('cancel') }}
    >
      <S.ModalContent>
        <ModalValidationErrors pageLayoutContentRef={pageLayoutContentRef} />
        <Typography variant="body1Bold">{t('enterUserEmailsAndSelectTheirRoleBelow')}:</Typography>
        <S.FormWrapper>
          <Select
            placeholder={t('enterOneMoreEmailAddresses')}
            onChange={onEmailsChange}
            onBlur={onEmailBlur}
            // onKeyDown={onEmailKeyDown}
            value={emails}
            size="lg"
            isMulti
            helperText={
              <Trans i18nKey="inviteUserEmailInputHelperText" components={{ b: <b /> }} />
            }
            styles={{
              menu: () => ({
                right: 0,
                left: 'auto!important'
              }),
              control: () => ({
                width: 600,
                minHeight: 40,
                height: 'max-content',
                minWidth: 'auto',
                padding: '8px 0',
                overflow: 'scroll'
              }),
              placeholder: () => ({
                left: 20,
                position: 'absolute',
                top: 12
              }),
              value: () => ({
                gap: 6
              })
            }}
            components={{
              Input: ReactSelectComponents.Input,
              Dropdown: () => null,
              DropdownIndicator: () => null,
              Control: ReactSelectComponents.Control,
              Option: ReactSelectComponents.Option,
              MultiValue: ReactSelectComponents.MultiValue,
              ValueContainer: ReactSelectComponents.ValueContainer,
              Menu: () => null,
              MenuList: ReactSelectComponents.MenuList,
              Placeholder: ReactSelectComponents.Placeholder
            }}
          />
          <RoleSelect
            value={role}
            onChange={setRole}
            menuWidth={300}
            size="lg"
            error={
              !role &&
              modalErrors?.warning &&
              modalErrors.warning[0]?.props?.i18nKey === 'errors.noEnteredInviteRole'
            }
            styles={{
              menu: () => ({
                right: 0,
                left: 'auto!important'
              })
            }}
          />
        </S.FormWrapper>
      </S.ModalContent>
    </Modal>
  );
}

InviteUserModal.propTypes = {
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func
};

export { InviteUserModal };
